// Relative + explicit extension: this module is also loaded by `node --test`
// (no Vite alias resolution there).
import type {
  AppFeedbackCategory,
  AppFeedbackDiscardReason,
  AppFeedbackPriority,
  AppFeedbackReviewListFilter,
  AppFeedbackReviewStatus,
  AppFeedbackSystemReleaseReason,
  DiscardAppFeedbackDto,
  ReleaseAppFeedbackDto,
} from "breuer-supercharged-sdk";
import { backendWallClockToInstant } from "../../utils/format_backend_datetime.ts";

/** Ab so vielen Tagen Wartezeit wird eine offene Meldung farbig markiert. */
export const FEEDBACK_OVERDUE_DAYS = 7;

export const FEEDBACK_MESSAGE_MIN_LENGTH = 3;
export const FEEDBACK_MESSAGE_MAX_LENGTH = 5000;
export const FEEDBACK_NOTE_MAX_LENGTH = 2000;

const DAY_MS = 24 * 60 * 60 * 1000;

/** `createdAt` ist `DEFAULT now()` (Berliner Wanduhr) → erst in den Instant übersetzen (#200). */
export function feedbackWaitingSince(createdAt: string | null | undefined): Date | null {
  return backendWallClockToInstant(createdAt);
}

export function isFeedbackOverdue(createdAt: string | null | undefined, now: Date = new Date()): boolean {
  const since = feedbackWaitingSince(createdAt);
  if (!since) return false;
  return now.getTime() - since.getTime() >= FEEDBACK_OVERDUE_DAYS * DAY_MS;
}

export interface ReviewTab {
  key: AppFeedbackReviewListFilter;
  label: string;
}

export const REVIEW_TABS: readonly ReviewTab[] = [
  { key: "pending_review", label: "Offen" },
  { key: "released", label: "Weitergegeben" },
  { key: "discarded", label: "Verworfen" },
  { key: "all", label: "Alle" },
];

export interface StatusMeta {
  label: string;
  /** antd Tag preset color. */
  color: string;
}

const STATUS_META: Record<AppFeedbackReviewStatus, StatusMeta> = {
  direct: { label: "Direkt gesendet", color: "blue" },
  pending_review: { label: "Offen", color: "gold" },
  released: { label: "Weitergegeben", color: "green" },
  discarded: { label: "Verworfen", color: "default" },
};

export function reviewStatusMeta(status: AppFeedbackReviewStatus): StatusMeta {
  return STATUS_META[status] ?? { label: status, color: "default" };
}

const PRIORITY_META: Record<AppFeedbackPriority, StatusMeta> = {
  normal: { label: "Normal", color: "default" },
  high: { label: "Hoch", color: "orange" },
  urgent: { label: "Dringend", color: "red" },
};

export const PRIORITY_OPTIONS = (Object.keys(PRIORITY_META) as AppFeedbackPriority[]).map((value) => ({
  value,
  label: PRIORITY_META[value].label,
}));

export function priorityMeta(priority: AppFeedbackPriority | null | undefined): StatusMeta | null {
  return priority ? (PRIORITY_META[priority] ?? { label: priority, color: "default" }) : null;
}

const DISCARD_REASON_LABELS: Record<AppFeedbackDiscardReason, string> = {
  duplicate: "Doppelt",
  not_a_bug: "Kein Fehler / erklärt",
  moved_to_openproject: "In OpenProject übernommen",
  other: "Sonstiges",
};

export const DISCARD_REASON_OPTIONS = (Object.keys(DISCARD_REASON_LABELS) as AppFeedbackDiscardReason[]).map(
  (value) => ({ value, label: DISCARD_REASON_LABELS[value] }),
);

export function discardReasonLabel(reason: AppFeedbackDiscardReason | null | undefined): string {
  return reason ? (DISCARD_REASON_LABELS[reason] ?? reason) : "";
}

export function isMessageLengthValid(message: string): boolean {
  const length = message.trim().length;
  return length >= FEEDBACK_MESSAGE_MIN_LENGTH && length <= FEEDBACK_MESSAGE_MAX_LENGTH;
}

export interface ReleaseFormValues {
  /** „Bearbeiten & freigeben“: Nachricht und Kategorie dürfen sich geändert haben. */
  edit: boolean;
  original: { message: string; category: AppFeedbackCategory };
  /** Nur im Bearbeiten-Modus im Formular vorhanden. */
  message?: string | null;
  category?: AppFeedbackCategory | null;
  priority?: AppFeedbackPriority | null;
  note?: string | null;
}

/** Nur geänderte Felder gehen als Bearbeitung mit; leere Priorität/Notiz werden weggelassen. */
export function buildReleasePayload(values: ReleaseFormValues): ReleaseAppFeedbackDto {
  const note = (values.note ?? "").trim();
  const message = values.edit && values.message != null ? values.message.trim() : null;
  const messageChanged = message !== null && message !== values.original.message.trim();
  const categoryChanged = values.edit && !!values.category && values.category !== values.original.category;
  return {
    ...(values.priority ? { priority: values.priority } : {}),
    ...(note ? { reviewerNote: note } : {}),
    ...(messageChanged ? { message: message! } : {}),
    ...(categoryChanged ? { category: values.category! } : {}),
  };
}

export function buildDiscardPayload(reason: AppFeedbackDiscardReason, note?: string | null): DiscardAppFeedbackDto {
  const trimmed = (note ?? "").trim();
  return { reason, ...(trimmed ? { note: trimmed } : {}) };
}

export interface AlreadyDecided {
  message: string;
  reviewStatus: AppFeedbackReviewStatus | null;
  decidedByName: string | null;
  decidedAt: string | null;
  systemReleaseReason: AppFeedbackSystemReleaseReason | null;
}

/** 409 „bereits entschieden“ aus einem fehlgeschlagenen SDK-Aufruf; sonst `null`. */
export function parseAlreadyDecided(error: unknown): AlreadyDecided | null {
  if (typeof error !== "object" || error === null || !("response" in error)) return null;
  const response = (error as { response?: { status?: number; data?: unknown } }).response;
  if (response?.status !== 409) return null;
  const data = (typeof response.data === "object" && response.data !== null ? response.data : {}) as Record<
    string,
    unknown
  >;
  const reviewStatus = typeof data.reviewStatus === "string" ? (data.reviewStatus as AppFeedbackReviewStatus) : null;
  const decidedByName = typeof data.decidedByName === "string" ? data.decidedByName : null;
  const decidedAt = typeof data.decidedAt === "string" ? data.decidedAt : null;
  const systemReleaseReason = isSystemReleaseReason(data.systemReleaseReason) ? data.systemReleaseReason : null;
  const message =
    typeof data.message === "string" && data.message.trim()
      ? data.message.trim()
      : fallbackAlreadyDecidedMessage(reviewStatus, decidedByName);
  return { message, reviewStatus, decidedByName, decidedAt, systemReleaseReason };
}

function fallbackAlreadyDecidedMessage(status: AppFeedbackReviewStatus | null, by: string | null): string {
  const verb = status === "discarded" ? "verworfen" : "weitergegeben";
  const actor = by ? `von ${by} ` : status === "discarded" ? "" : "vom System ";
  return `Wurde bereits ${actor}${verb}.`;
}

function isSystemReleaseReason(value: unknown): value is AppFeedbackSystemReleaseReason {
  return value === "review_disabled" || value === "no_reviewers";
}

export interface SystemReleaseLabel {
  /** Kurzes Tag für Tabellen. */
  tag: string;
  /** Tooltip am Tag. */
  tooltip: string;
  /** Grund als Satz für die Detailansicht. */
  sentence: string;
}

/** System-Weitergabe (Vorprüfung abgeschaltet / kein Prüfer mehr); `null` bei normaler Entscheidung. Das SDK typisiert das Feld nicht-nullable, zur Laufzeit kommt `null`. */
export function systemReleaseLabel(reason: AppFeedbackSystemReleaseReason | null | undefined): SystemReleaseLabel | null {
  switch (reason) {
    case "review_disabled":
      return {
        tag: "System",
        tooltip: "Automatisch weitergegeben – Vorprüfung abgeschaltet",
        sentence: "Automatisch weitergegeben, weil die Vorprüfung abgeschaltet wurde.",
      };
    case "no_reviewers":
      return {
        tag: "System",
        tooltip: "Automatisch weitergegeben – kein Prüfer mehr",
        sentence: "Automatisch weitergegeben, weil kein Prüfer mehr vorhanden war.",
      };
    default:
      return null;
  }
}

export interface ReviewSettingsDraft {
  reviewEnabled: boolean;
  reviewerUserIds: string[];
}

export interface ReviewSettingsContext {
  currentUserId: string | null | undefined;
  savedReviewerUserIds: string[];
}

/** Wie der Server: aktiv ⇒ mindestens ein Prüfer. */
export function validateReviewSettings(draft: ReviewSettingsDraft, ctx: ReviewSettingsContext): string | null {
  if (!draft.reviewEnabled || draft.reviewerUserIds.length > 0) return null;
  const onlyMeBefore = ctx.savedReviewerUserIds.length === 1 && ctx.savedReviewerUserIds[0] === ctx.currentUserId;
  return onlyMeBefore
    ? "Sie sind der letzte Prüfer. Schalten Sie die Vorprüfung aus oder tragen Sie zuerst eine weitere Person ein."
    : "Für die Vorprüfung ist mindestens ein Prüfer nötig. Tragen Sie einen Prüfer ein oder schalten Sie die Vorprüfung aus.";
}

/** Der aktuelle Nutzer war Prüfer und steht nach dem Speichern nicht mehr in der Liste. */
export function removesCurrentUserAsReviewer(draft: ReviewSettingsDraft, ctx: ReviewSettingsContext): boolean {
  if (!ctx.currentUserId) return false;
  return ctx.savedReviewerUserIds.includes(ctx.currentUserId) && !draft.reviewerUserIds.includes(ctx.currentUserId);
}

export function reviewSettingsDirty(draft: ReviewSettingsDraft, saved: ReviewSettingsDraft): boolean {
  if (draft.reviewEnabled !== saved.reviewEnabled) return true;
  const a = [...draft.reviewerUserIds].sort();
  const b = [...saved.reviewerUserIds].sort();
  return a.length !== b.length || a.some((id, i) => id !== b[i]);
}

/** Bei „Sonstiges“ ist die Notiz Pflicht, sonst wüsste niemand, worum es ging. */
export function isDiscardNoteRequired(reason: AppFeedbackDiscardReason | null | undefined): boolean {
  return reason === "other";
}

/** Letzte gültige Seite einer Liste; schrumpft `total`, darf `page` nicht dahinter hängen bleiben. */
export function lastReviewPage(total: number | null | undefined, pageSize: number): number {
  return Math.max(1, Math.ceil((total ?? 0) / pageSize));
}

export function clampReviewPage(page: number, total: number | null | undefined, pageSize: number): number {
  return Math.min(Math.max(1, page), lastReviewPage(total, pageSize));
}

/** HTTP 403 aus einem fehlgeschlagenen SDK-Aufruf (kein Prüfer mehr). */
export function isForbiddenError(error: unknown): boolean {
  if (typeof error !== "object" || error === null || !("response" in error)) return false;
  return (error as { response?: { status?: number } }).response?.status === 403;
}

/** Polling-Intervall; bei 403 oder inaktiver Seite sofort aus. */
export function reviewPollInterval(opts: { error: unknown; active: boolean; intervalMs: number }): number | false {
  return opts.active && !isForbiddenError(opts.error) ? opts.intervalMs : false;
}

/** Retry-Regel: 403 nie wiederholen, sonst höchstens zweimal. */
export function shouldRetryReviewRequest(failureCount: number, error: unknown): boolean {
  return !isForbiddenError(error) && failureCount < 2;
}

/** Würde Speichern die Vorprüfung inaktiv machen (Schalter aus oder keine Prüfer übrig)? */
export function savingDeactivatesReview(draft: ReviewSettingsDraft): boolean {
  return !draft.reviewEnabled || draft.reviewerUserIds.length === 0;
}

/** Rückfrage vor dem Speichern; `null`, wenn keine offenen Meldungen automatisch weitergegeben würden. */
export function autoReleaseConfirmText(draft: ReviewSettingsDraft, pendingCount: number | null | undefined): string | null {
  const n = pendingCount ?? 0;
  if (n <= 0 || !savingDeactivatesReview(draft)) return null;
  return n === 1
    ? "1 offene Meldung wird dann automatisch an die Entwickler weitergegeben. Fortfahren?"
    : `${n} offene Meldungen werden dann automatisch an die Entwickler weitergegeben. Fortfahren?`;
}

/** Erfolgsmeldung nach dem Speichern der Einstellungen. */
export function settingsSavedMessage(autoReleasedCount: number | null | undefined): string {
  const n = autoReleasedCount ?? 0;
  if (n <= 0) return "Einstellungen gespeichert.";
  return n === 1
    ? "Gespeichert. 1 offene Meldung wurde an die Entwickler weitergegeben."
    : `Gespeichert. ${n} offene Meldungen wurden an die Entwickler weitergegeben.`;
}
